import { queryOptions } from "@tanstack/react-query";
import { call } from "./api";

// Signing in and the account (docs/api.md §9.26). The portal never sees a password again after sending
// it, nor the secret of the app after it is linked; the recovery codes it shows once.

export type Language = "nl" | "en" | "de";

export interface Company {
  id: string;
  code: string;
  name: string;
  city: string | null;
}

export interface Me {
  user: { id: string; email: string; name: string; language: Language };
  parties: Company[];
  party: Company | null;
  needsParty: boolean;
  /** What the portal shows for the company of the session: Voorraad only when Willems turned it on (B6). */
  features: { stock: boolean };
  mustRelink: boolean;
  recoveryCodesLeft: number;
}

export const meQuery = queryOptions({
  queryKey: ["partner", "me"],
  queryFn: () => call<Me>("GET", "/partner/auth/me"),
  staleTime: 60_000,
});

export function signIn(email: string, password: string) {
  return call<{ next: "code" }>("POST", "/partner/auth/login", { email, password });
}

export function signInCode(code: string) {
  return call<Me>("POST", "/partner/auth/login/code", { code });
}

export function signInRecoveryCode(recoveryCode: string) {
  return call<Me>("POST", "/partner/auth/login/code", { recoveryCode });
}

export function signOut() {
  return call<void>("POST", "/partner/auth/logout");
}

export function chooseCompany(partyId: string) {
  return call<Me>("POST", "/partner/auth/party", { partyId });
}

export function setLanguage(language: Language) {
  return call<Me>("PATCH", "/partner/auth/me", { language });
}

export function changePassword(currentPassword: string, newPassword: string) {
  return call<void>("POST", "/partner/auth/password", { currentPassword, newPassword });
}

export interface NewSecret {
  secret: string;
  otpauthUri: string;
}

/** A new phone: the password, except straight after a recovery code. */
export function startNewPhone(password?: string) {
  return call<NewSecret>("POST", "/partner/auth/totp", password === undefined ? {} : { password });
}

export function confirmNewPhone(code: string) {
  return call<Me>("POST", "/partner/auth/totp/confirm", { code });
}

export function renewRecoveryCodes(password: string) {
  return call<{ recoveryCodes: string[]; recoveryCodesLeft: number }>("POST", "/partner/auth/recovery-codes", { password });
}

// ─── The invitation and a new password ──────────────────────────────────────────────────────────

export interface Invitation {
  email: string;
  name: string;
  language: Language;
  companies: string[];
  expiresAt: string;
  next: "password" | "app";
}

export function openInvitation(token: string) {
  return call<Invitation>("POST", "/partner/auth/invitation", { token });
}

export function invitationPassword(token: string, password: string) {
  return call<NewSecret>("POST", "/partner/auth/invitation/password", { token, password });
}

export function invitationConfirm(token: string, code: string) {
  return call<Me & { recoveryCodes: string[] }>("POST", "/partner/auth/invitation/confirm", { token, code });
}

export function requestPasswordReset(email: string) {
  return call<void>("POST", "/partner/auth/password-reset", { email });
}

export function confirmPasswordReset(token: string, password: string, code: string) {
  return call<Me>("POST", "/partner/auth/password-reset/confirm", { token, password, code });
}

/** The token of a link in a mail: after #t=, the part of a link no server ever sees. */
export function tokenFromHash(hash: string): string | null {
  const match = /(?:^#?|&)t=([A-Za-z0-9_-]{20,200})/.exec(hash);
  return match ? match[1]! : null;
}
